// frontend/src/shared/pricing/categoryMapping.js
// PURE MAP FUNCTIONS - ZERO DEPENDENCIES

import { VEHICLE_CATEGORIES, allVehicleCategories } from "./constants.js";

/**
 * Map a detected car category key to the generic vehicle category name.
 * Handles migration fallbacks from legacy keys to the new 13 schema.
 *
 * @param {string} categoryKey - From detectCarCategory() e.g. "SEDAN_5_STD", "HATCHBACK"
 * @param {string} [modelName] - Raw car model string (legacy param, kept for signature compat)
 * @returns {string} - Vehicle category name matching VEHICLE_CATEGORIES
 */
export function mapToAssociationCategory(categoryKey, modelName = "") {
  if (!categoryKey) return "SEDAN_5_STD";

  /* Every category in force is its own association category — including the ones an
     admin created this morning.
     This was a hand-written list of nineteen keys, frozen before categories became
     something an admin could add. Anything outside it fell to the default at the
     bottom of this function and was priced as a standard saloon, silently. Reading the
     live list instead means a new category is understood everywhere the moment it
     exists, which is the only version of this that stays true. */
  if (allVehicleCategories().includes(categoryKey)) {
    return categoryKey;
  }

  // Fallback map legacy keys
  const CATEGORY_MAP = {
    HATCHBACK:       "HATCHBACK_5",
    MINI:            "HATCHBACK_5",
    SEDAN:           "SEDAN_5_STD",
    MINI_SUV:        "MINI_SUV_5",
    SUV_5:           "MINI_SUV_5",
    SUV:             "SUV_7_STD",
    SUV_7:           "SUV_7_STD",
    MPV:             "SUV_7_STD",
    MINIVAN:         "SUV_7_STD",
    MPV_LUX:         "SUV_7_COMFORT",
    LUXURY:          "SUV_7_COMFORT",
    TEMPO_TRAVELLER: "TEMPO_TRAVELLER_11",
    MINI_BUS:        "MINI_BUS_13"
  };

  const mapped = CATEGORY_MAP[categoryKey];
  if (mapped) return mapped;

  /* Nothing recognised it. The default below is a guess, and a quiet guess is what
     made the original bug survive: an unknown key produced a plausible price from the
     wrong tier and nobody could tell. Say so, so it is findable.
     Note this is also reached for a live category before the admin config has loaded,
     which is why nothing that prices a car depends on this answer alone — the callers
     ask the city's own rate card for the exact key first. */
  if (typeof console !== "undefined" && console.warn) {
    console.warn(`[categoryMapping] Unknown category "${categoryKey}" - falling back to SEDAN_5_STD.`);
  }
  return "SEDAN_5_STD";
}

/**
 * Get the vehicle category index (0-11) for a category key.
 * Returns -1 if no match found.
 */
export function getAssociationVehicleIndex(categoryKey, modelName = "") {
  const assocCat = mapToAssociationCategory(categoryKey, modelName);
  // The live list, so a custom category has a position rather than -1. Nothing reads
  // this today; it is kept correct rather than left as a trap for whoever does.
  return allVehicleCategories().indexOf(assocCat);
}
